import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import {
  Navigate,
  Link as RouterLink,
} from "react-router-dom";

import ClientAuthLayout from "../components/ClientAuthLayout";
import { useDataStore } from "../dataStore";
import { useClientPortal } from "../clientPortal/ClientPortalContext";
import { CLIENT_PORTAL_PATHS } from "../clientPortal/clientPortalConstants";
import { DEFAULT_BRAND_THEME } from "../themeTokens";

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export default function ClientForgotPasswordPage() {
  const { brandTheme, t } = useDataStore();
  const c = { ...DEFAULT_BRAND_THEME, ...brandTheme };
  const {
    configReady,
    enabled,
    isAuthenticated,
    requestPasswordReset,
  } = useClientPortal();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [failed, setFailed] = useState(false);

  if (configReady === false) {
    return (
      <Box
        role="status"
        aria-label={t("portal.guard.restoring")}
        sx={{ minHeight: 240, display: "grid", placeItems: "center" }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!enabled) return <Navigate to="/" replace />;
  if (isAuthenticated) {
    return <Navigate to={CLIENT_PORTAL_PATHS.account} replace />;
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !validEmail(email)) return;
    setSubmitting(true);
    setFailed(false);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setComplete(true);
    } catch (error) {
      if (error?.status >= 500 || error?.status === 0) {
        setFailed(true);
      } else {
        // Keep account existence and request throttling private.
        setComplete(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const aside = (
    <Stack spacing={2.25}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "16px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      >
        <LoginRoundedIcon aria-hidden="true" />
      </Box>
      <Box>
        <Typography component="h2" variant="h5" fontWeight={950}>
          {t("portal.forgot.rememberedTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          {t("portal.forgot.rememberedText")}
        </Typography>
      </Box>
      <Button
        component={RouterLink}
        to={CLIENT_PORTAL_PATHS.login}
        variant="outlined"
        sx={{
          alignSelf: "flex-start",
          minHeight: 46,
          px: 2.25,
          borderRadius: "14px",
        }}
      >
        {t("portal.register.signIn")}
      </Button>
    </Stack>
  );

  return (
    <ClientAuthLayout aside={aside}>
      <Stack spacing={{ xs: 2.25, sm: 2.75 }}>
        <Stack
          spacing={1.1}
          alignItems={{ xs: "center", sm: "flex-start" }}
          textAlign={{ xs: "center", sm: "left" }}
        >
          <Box
            sx={{
              width: 62,
              height: 62,
              borderRadius: "19px",
              display: "grid",
              placeItems: "center",
              bgcolor: c.primary,
              color: c.onPrimary,
              boxShadow: "0 16px 34px rgba(247,181,0,0.22)",
            }}
          >
            {complete ? (
              <CheckCircleRoundedIcon />
            ) : (
              <LockResetRoundedIcon />
            )}
          </Box>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              color: c.text,
              fontSize: { xs: 30, sm: 38 },
              lineHeight: 1.08,
            }}
          >
            {complete
              ? t("portal.forgot.sentTitle")
              : t("portal.forgot.title")}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ maxWidth: 520, lineHeight: 1.6 }}
          >
            {complete
              ? t("portal.forgot.sentText")
              : t("portal.forgot.subtitle")}
          </Typography>
        </Stack>

        {failed ? (
          <Alert severity="error">{t("portal.forgot.failed")}</Alert>
        ) : null}

        {!complete ? (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                id="client-reset-email"
                label={t("portal.register.email")}
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setFailed(false);
                }}
                autoComplete="email"
                required
                fullWidth
                disabled={submitting}
                error={Boolean(email) && !validEmail(email)}
                helperText={
                  email && !validEmail(email)
                    ? t("portal.register.emailInvalid")
                    : " "
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !validEmail(email)}
                sx={{
                  minHeight: 52,
                  borderRadius: "15px",
                  bgcolor: c.primary,
                  color: c.onPrimary,
                  "&:hover": { bgcolor: c.primary },
                }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    {t("portal.forgot.pending")}
                  </>
                ) : (
                  t("portal.forgot.submit")
                )}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Button
            component={RouterLink}
            to={CLIENT_PORTAL_PATHS.login}
            variant="contained"
            sx={{
              minHeight: 50,
              borderRadius: "15px",
              bgcolor: c.primary,
              color: c.onPrimary,
              "&:hover": { bgcolor: c.primary },
            }}
          >
            {t("portal.register.signIn")}
          </Button>
        )}
      </Stack>
    </ClientAuthLayout>
  );
}
